import React, { useState, useEffect, useRef } from 'react';
import { autoTranslator } from '../lib/translations/autoTranslator';
import type { SupportedLanguages } from '../i18n';
import { logger } from '../lib/logger';

interface UseAutoTranslationProps {
  description: string;
  currentLanguage: SupportedLanguages;
  context: {
    tableId: string;
    itemId: string;
    sectionType: 'event-api' | 'event' | 'standard' | 'liste';
    idShow?: string;
    show_id?: string;
    source_hash?: string;
    contentType?: 'description' | 'offer';
  };
  existingTranslations?: Record<string, string>;
}

export const useAutoTranslation = ({
  description,
  currentLanguage,
  context,
  existingTranslations = {}
}: UseAutoTranslationProps) => {
  const [translatedDescription, setTranslatedDescription] = useState<string | null>(null);
  const [translating, setTranslating] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const translationLock = useRef<boolean>(false);
  const hasValidTranslation = useRef<boolean>(false);
  const isActivelyTranslating = useRef<boolean>(false);
  const translatingTimerRef = useRef<number | null>(null);

  // Vérifier si on a une traduction valide dans les traductions existantes
  const checkExistingTranslation = () => {
    const existingTranslation = existingTranslations[currentLanguage];
    const isValid = !!(existingTranslation && 
      existingTranslation.trim() !== '' && 
      existingTranslation !== '\n');

    hasValidTranslation.current = isValid;

    return isValid;
  };

  // Effectuer la traduction quand la langue change
  useEffect(() => {
    // Réinitialiser l'état quand la langue change
    setTranslatedDescription(null);
    setTranslating(false);
    setError(null);
    translationLock.current = false;
    isActivelyTranslating.current = false;
    
    // Clear any existing timer
    if (translatingTimerRef.current) {
      clearTimeout(translatingTimerRef.current);
      translatingTimerRef.current = null;
    }

    // Vérifier d'abord les traductions existantes
    if (checkExistingTranslation()) {
      return;
    }

    // Ne rien faire si on est déjà en train de traduire
    if (translationLock.current) {
      return;
    }

    let isMounted = true;
    translationLock.current = true;

    const performTranslation = async () => {
      try {
        if (!isMounted) return;
        
        // Only set translating to true when we're actually going to use the API
        // Not when we're just checking for existing translations
        isActivelyTranslating.current = true;
        
        // Add a small delay before showing the translation indicator
        // This prevents the indicator from flashing briefly when checking for translations
        translatingTimerRef.current = window.setTimeout(() => {
          if (isActivelyTranslating.current && isMounted) {
            setTranslating(true);
          }
        }, 300); // 300ms delay

        setError(null);

        const translated = await autoTranslator.translateDescription(
          description,
          currentLanguage,
          {
            ...context,
            contentType: context.contentType || 'description'
          }
        );

        if (translated && isMounted) {
          setTranslatedDescription(translated);
        }
      } catch (err) {
        if (!isMounted) return;
        const error = err instanceof Error ? err : new Error('Translation failed');
        setError(error);
        logger.error('Translation error', {
          category: 'Translation',
          data: { 
            error,
            itemId: context.itemId,
            language: currentLanguage,
            contentType: context.contentType || 'description',
            sectionType: context.sectionType
          }
        });
      } finally {
        if (isMounted) {
          setTranslating(false);
          translationLock.current = false;
          isActivelyTranslating.current = false;
          
          // Clear the timer
          if (translatingTimerRef.current) {
            clearTimeout(translatingTimerRef.current);
            translatingTimerRef.current = null;
          }
        }
      }
    };

    // Délai de 500ms avant de démarrer la traduction
    const timeoutId = setTimeout(performTranslation, 500);

    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
      translationLock.current = false;
      isActivelyTranslating.current = false;
      
      // Clear the timer
      if (translatingTimerRef.current) {
        clearTimeout(translatingTimerRef.current);
        translatingTimerRef.current = null;
      }
    };
  }, [description, currentLanguage, context.itemId, context.sectionType, context.idShow, context.show_id, context.contentType]);

  const translate = async (): Promise<string | null> => {
    // Vérifier d'abord les traductions existantes
    if (checkExistingTranslation()) {
      return existingTranslations[currentLanguage];
    }

    if (translationLock.current) {
      return null;
    }

    try {
      translationLock.current = true;
      isActivelyTranslating.current = true;
      
      // Add a small delay before showing the translation indicator
      translatingTimerRef.current = window.setTimeout(() => {
        if (isActivelyTranslating.current) {
          setTranslating(true);
        }
      }, 300); // 300ms delay
      
      setError(null);

      const translated = await autoTranslator.translateDescription(
        description,
        currentLanguage,
        {
          ...context,
          contentType: context.contentType || 'description'
        }
      );

      if (translated) {
        setTranslatedDescription(translated);
      }

      return translated;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Translation failed');
      setError(error);
      return null;
    } finally {
      setTranslating(false);
      translationLock.current = false;
      isActivelyTranslating.current = false;
      
      // Clear the timer
      if (translatingTimerRef.current) {
        clearTimeout(translatingTimerRef.current);
        translatingTimerRef.current = null;
      }
    }
  };

  return {
    translatedDescription,
    translating, // Now controlled with a delay
    error,
    translate,
    hasValidTranslation: hasValidTranslation.current
  };
};